import React, { useEffect, useRef } from 'react'
import { init, AuthType, LiveboardEmbed } from '@thoughtspot/visual-embed-sdk'
import { ThoughtSpotTokenResponse, getThoughtSpotToken } from 'modules/domain/report/managers'
import { Report } from 'modules/domain/report/types'
import styled from 'styled-components'

const ThoughtSpotContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px;
`

interface ThoughtSpotReportProps {
  report: Report
  tokenData: ThoughtSpotTokenResponse
  fullScreen?: boolean
}

const ThoughtSpotReport: React.FC<ThoughtSpotReportProps> = ({ report, tokenData, fullScreen = false }) => {
  const embedRef = useRef<HTMLDivElement>(null)
  const embedInstanceRef = useRef<LiveboardEmbed | null>(null)

  useEffect(() => {
    if (!tokenData?.token || !tokenData?.server_uri || !embedRef.current || !report.url) {
      return
    }

    // Initialize ThoughtSpot SDK (only needs to be called once, but safe to call multiple times)
    init({
      thoughtSpotHost: tokenData.server_uri,
      authType: AuthType.TrustedAuthTokenCookieless,
      autoLogin: true,
      disableLoginRedirect: true, // Prevent redirects that might cause CORS issues
      getAuthToken: async () => {
        // Fetch a fresh token each time getAuthToken is called
        // ThoughtSpot requires a new token for each call to prevent duplicate token errors
        try {
          const response = await getThoughtSpotToken()
          return response.token || ''
        } catch (error) {
          console.error('Error fetching ThoughtSpot token:', error)
          return ''
        }
      },
    })

    // Extract liveboard/view ID from the report URL
    // The URL format could be:
    // - Full URL: https://thoughtspot.example.com/#/pinboard/{id} or /#/view/{id}
    // - Path: /pinboard/{id} or /view/{id}
    // - Just the ID: {id}
    const reportUrl = report.url.trim()
    let liveboardId = reportUrl

    // Try to extract ID from various URL patterns
    const patterns = [
      /[#/]pinboard\/([^/?#]+)/i, // #/pinboard/{id} or /pinboard/{id}
      /[#/]view\/([^/?#]+)/i, // #/view/{id} or /view/{id}
      /pinboardId=([^&]+)/i, // Query parameter
      /liveboardId=([^&]+)/i, // Query parameter
    ]

    for (const pattern of patterns) {
      const match = reportUrl.match(pattern)
      if (match && match[1]) {
        liveboardId = match[1]
        break
      }
    }

    // If still contains slashes, extract the last meaningful part
    if (liveboardId.includes('/')) {
      const parts = liveboardId.split('/').filter(Boolean)
      // Skip common path segments
      const meaningfulParts = parts.filter(
        (part) => !['pinboard', 'view', 'liveboard', 'v1', 'callosum'].includes(part.toLowerCase()),
      )
      if (meaningfulParts.length > 0) {
        liveboardId = meaningfulParts[meaningfulParts.length - 1]
      } else {
        liveboardId = parts[parts.length - 1]
      }
    }

    // Clean up any query parameters or fragments
    liveboardId = liveboardId.split('?')[0].split('#')[0]

    // Clean up previous embed if it exists
    if (embedInstanceRef.current && embedRef.current) {
      embedRef.current.innerHTML = ''
      embedInstanceRef.current = null
    }

    // Create and render the embed
    try {
      const embed = new LiveboardEmbed(embedRef.current, {
        liveboardId: liveboardId,
        frameParams: {
          width: '100%',
          height: fullScreen ? '100vh' : '100%',
        },
      })

      embedInstanceRef.current = embed
      embed.render()
    } catch (error) {
      console.error('Error rendering ThoughtSpot embed:', error)
    }

    // Cleanup on unmount
    return () => {
      if (embedRef.current) {
        embedRef.current.innerHTML = ''
      }
      embedInstanceRef.current = null
    }
  }, [report.url, tokenData, fullScreen])

  if (!tokenData?.token || !tokenData?.server_uri) {
    return null
  }

  return <ThoughtSpotContainer ref={embedRef} />
}

export default ThoughtSpotReport
